/**
 * RequireSuperAdmin — route guard for prototype/super-admin-only pages.
 *
 * Currently used to gate the entire Marketing Hub module while it's
 * incomplete. Designers iterating on `/pages/marketing/*` do NOT need to
 * add any extra check inside their page — wrapping the <Route> with this
 * guard is sufficient.
 *
 * Semantics (mirrors RequireCapability):
 *   • Auth still loading              → neutral spinner.
 *   • user.role is super_admin (or
 *     a normalized alias)             → render children.
 *   • Anything else                   → "Access denied" state with a
 *                                       Dashboard link.
 */
import { Link } from "react-router-dom";
import { ShieldOff } from "lucide-react";
import { useAuth } from "@/App";
import { isSuperAdmin } from "@/lib/permissions";

export default function RequireSuperAdmin({ children }) {
  const { user, loading } = useAuth() || {};

  if (loading) {
    return (
      <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}
           data-testid="super-admin-loading">
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 22, height: 22, border: "2.5px solid var(--blue)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 8px" }} />
          <p style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Checking access…</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (isSuperAdmin(user?.role)) return children;

  return (
    <div data-testid="access-denied-super-admin"
         style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}>
      <div style={{ maxWidth: 380, textAlign: "center" }}>
        <div style={{ width: 48, height: 48, margin: "0 auto 14px", borderRadius: "50%",
                      background: "#FCEBEB", display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#A32D2D" }}>
          <ShieldOff style={{ width: 22, height: 22 }} />
        </div>
        <h2 style={{ fontSize: 17, fontWeight: 650, marginBottom: 6 }}>Access denied</h2>
        <p style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 18, lineHeight: 1.5 }}>
          This area is currently in active development and restricted to Super
          Admins. If you need access, please contact your administrator.
        </p>
        <Link to="/" data-testid="access-denied-super-admin-dashboard-link"
              style={{ display: "inline-block", background: "#2563eb", color: "#fff",
                       padding: "8px 18px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                       textDecoration: "none" }}>
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
}
